<template lang="pug">
#pricing(:class="{ 'is-zh': locale === 'zh' }")
  main.pricing-main
    .pricing-layout
      .pricing-flow
        div(v-if="mode === 'topUp'")
          section.step-panel.top-up
            .section-heading-row
              .title
                h1
                  span 1
                  | {{ $t('pricing.add_credit') }}
              div.extra-option
                Button.unselectable.rev-ghost(plain @click="mode = 'buyService'")
                  Icon(name="mdi:package-variant-closed-plus" class="mr-2")
                  | {{ $t('pricing.purchase_service') }}
            .pricing-surface
              PricingPageTopUp(v-model="topUpAmount")
        div(v-if="mode === 'buyService'")
          section.step-panel.select-cate#cate
            .section-heading-row
              .title
                h1
                  span 1
                  | {{ $t('pricing.choose_cate') }}
              div.extra-option(v-if="hasService || isAdmin || user.id === 289252")
                Button.unselectable.rev-ghost(plain @click="mode = 'topUp'")
                  Icon(name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" class="mr-2")
                  | {{ $t('pricing.add_credit') }}
            PricingPageCateOptions(v-model="selectedCate")
          section.step-panel.select-plan#plan
            .section-heading-row
              .title
                h1
                  span 2
                  | {{ $t(selectedTime > 0 ? 'pricing.choose_plan' : 'pricing.extra_data') }}
              PricingPageTimeOptions(v-if="selectedCate && !pricing.storeManagedPlan")
            .addon-strip(v-if="canShowAddonStrip"
              role="button"
              tabindex="0"
              :aria-label="$t(selectedTime > 0 ? addonSwitchLabelKey : 'pricing.back_to_plans')"
              :class="{ 'is-addon-mode': selectedTime === 0 }"
              @click="switchDataOrPlan"
              @keydown.enter.prevent="switchDataOrPlan"
              @keydown.space.prevent="switchDataOrPlan")
              .addon-copy
                .addon-icon
                  Icon(name="material-symbols:add-rounded")
                div
                  h3 {{ $t(selectedTime > 0 ? addonStripTitleKey : addonStripActiveTitleKey) }}
                  p {{ $t(selectedTime > 0 ? addonStripHintKey : 'pricing.addon_strip_active_hint') }}
              span.addon-action
                | {{ $t(selectedTime > 0 ? addonSwitchLabelKey : 'pricing.back_to_plans') }}
            #plan-list
              .pricing-surface.plan-empty(v-if="!selectedCate")
                p {{ $t('pricing.choose_cate_first') }}
              .pricing-surface.plan-empty.store-managed(v-else-if="pricing.storeManagedPlan")
                StoreManagedLine.justify-center(:managed="pricing.storeManagedPlan")
              div(v-else-if="selectedTime > 0")
                PricingPagePlanOptions(@contact-enterprise="openLiveChat")
                Button.enterprise-fallback(:disabled="contactingForEnterprisePricing" variant="secondary" @click="openLiveChat()" v-if="purchaseType.type !== 'topUp' && !useModernPricing")
                  | {{ $t('pricing.corp_purchase') }}
              div(v-else="currentService && currentService.extra")
                .pricing-surface
                  p(v-if="dataAddonValidUntil" class="mb-4 flex items-start gap-1.5 text-sm text-muted-foreground")
                    Icon(name="material-symbols:schedule-rounded" class="mt-0.5 size-4 shrink-0")
                    span {{ $t('purchase_dialog.data_valid_until', { time: dataAddonValidUntil }) }}
                  PricingPagePremiumData(:upgradePlan="upgradePlan")
        section.admin-operations(v-if='isAdmin')
          client-only
            UserPageAdminOperations
      aside.pricing-summary.summary#payment(:class="{ 'is-ready': showSummary, 'is-dock-only': summaryDockOnly }")
        .summary-card
          .summary-card-header
            h2 {{ $t('pricing.checkout_summary') }}
          .summary-empty(v-if="!showSummary")
            Icon(name="material-symbols:shopping-cart-outline-rounded")
            p {{ $t('pricing.checkout_empty_hint') }}
          template(v-else)
            .summary-block.payment-method(v-if="showPaymentMethodSelector")
              PricingPagePaymentOptions(:mode="mode" :cate="selectedCate")
            .summary-block.billing#billing-address-form(v-if="willShowBilling")
              UpdateBilling(ref="billingEditor" :show-title="false" toast-position="top-center")
            div#button-wrapper
              div#floating-container(ref="floatingContainer")
                .summary-block.details
                  PricingPageFeeBreakdown
        button.modern-pay-btn(v-if="showSummary" @click="pay" :disabled='confirming || dataAddonPurchaseBlocked' :class="{ loading: confirming, warning: purchaseButtonType === 'warning', downgrade: purchaseType.type === 'downgrade' }")
          Icon(name="material-symbols:electric-bolt-rounded" class="bolt-icon" v-if="!confirming && purchaseType.type === 'new_buy'")
          Icon(name="material-symbols:shopping-cart-checkout-rounded" class="cart-icon" v-if="!confirming && purchaseType.type !== 'downgrade' && purchaseType.type !== 'new_buy'")
          Icon(name="material-symbols:warning-rounded" class="warning-icon" v-if="!confirming && purchaseType.type === 'downgrade'")
          Icon(name="material-symbols:progress-activity" class="loading-icon" v-if="confirming")
          span {{ $t(`pricing.${purchaseType.type}`) }}
        div#checkout
      //- section.pricing-help
      //-   Separator.pricing-help-separator.my-8.mx-auto(style="width: 80%")
      //-   Faq(:faqItems="faqItems" :titleSize="'2rem'")
  SignupLoginModal(v-model="signModalVisible" :defaultState="defaultState")

  //- StripeCheckoutModal(v-model="stripeModalVisible" :session="stripeCheckoutSession")

  Dialog(v-model:open="showQr" class="qr-modal")
    DialogContent(class="max-w-[85%] w-[85%]")
      img(:src="qrData")

  //-     el-row(type="flex" justify="center" align="middle")
  //-       el-col(:sm="24")
  //-         p(v-if="['stripe_wechat', 'yaband_wechat'].includes(selectedPaymentMethod.name as string)")
  //-           | {{$t('pricing.stripe_wechat_qr_hint')}}
</template>

<script lang="ts" setup>
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { BILLING_TZ } from '@/utils/billingPeriod'
import {
  ALLPLATFORM_QUOTE_PLAN_IDENTIFIER,
  HOME_QUOTE_PLAN_IDENTIFIER,
  RELAY_DATA_ADDON_PLAN_IDENTIFIER,
  dataAddonPeriodEnd,
} from '@/utils/dataAddonPricing'
// import { Separator } from '@/components/ui/separator'
import { toast } from '~/utils/sonner'
definePageMeta({
  middleware: ['fullscreen-loading-start', 'restore-session-and-user', 'fullscreen-loading-stop']
})
const { t: $t, locale } = useI18n()
const dayjs = useDayjs()
const $route = useRoute()
const isPageQueryActive = usePageQueryActive()
const $utils = useUtils()
const pricingToast = (input: Parameters<typeof toast>[0]) => {
  if (typeof input === 'string') {
    return toast({ title: input, position: 'top-center' })
  }
  return toast({ ...input, position: input.position ?? 'top-center' })
}
const user = useUser()
const session = useVisitorSession()
const service = useUserService()
const $config = useRuntimeConfig().public
const pricing = usePricing()
const isAdmin = computed(() => session.isAdmin)

useHead({
  title: () => $t('pricing.page_title') + `${isAdmin.value ? (' | ' + user.id) : ''}`,
})
// if (!session.realAdmin && user.extra.suspend) {
//   console.log('account suspended', session.realAdmin, user.extra.suspend)
//   createError({
//     statusCode: 403,
//     message: 'errors.account_suspended.message',
//     data: 'errors.account_suspended.title',
//     fatal: true,
//   })
// }

const billingEditor = ref()
const contactingForEnterprisePricing = ref(false)
// const contactedForEnterprisePricing = ref(false)

// Toggle for modern pricing design
const useModernPricing = ref(true)

const querySyncEnabled = ref(false)
const applyingQuery = ref(false)
const pushingQuery = ref(false)
const pricingQueryKeys = new Set([
  'cate',
  'plan',
  'time',
  'mode',
  'promo',
  'promoCode',
  'amount',
  'pay',
  'payment',
  'method',
  'extraPremium',
  'extra_premium',
  'addonPlanId',
  'addon_plan_id',
  'allPre',
  'all_pre',
])

const normalizeQuery = (query: Record<string, any>) => {
  const normalized: Record<string, string> = {}
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value == null) {
      return
    }
    const scalar = Array.isArray(value) ? value[0] : value
    if (scalar == null || scalar === '') {
      return
    }
    normalized[key] = String(scalar)
  })
  return normalized
}

const buildPricingQuery = () => {
  const next: Record<string, string> = {}
  if (selectedCate.value) {
    next.cate = String(selectedCate.value)
  }
  const rawPlan = selectedPlan.value
  if (rawPlan !== null && rawPlan !== undefined && rawPlan !== '') {
    next.plan = !Number.isNaN(Number(rawPlan)) ? String(Number(rawPlan)) : String(rawPlan)
  }
  const rawTime = selectedTime.value
  const timeValue = rawTime !== null && rawTime !== undefined ? Number(rawTime) : NaN
  if (Number.isFinite(timeValue)) {
    next.time = String(timeValue)
  }
  if (mode.value) {
    next.mode = String(mode.value)
  }
  if (promoCode.value) {
    next.promo = String(promoCode.value)
  }
  if (mode.value === 'topUp') {
    const amountValue = Number(topUpAmount.value)
    if (Number.isFinite(amountValue) && amountValue > 0) {
      next.amount = String(Math.max(1, Math.ceil(amountValue)))
    }
  }
  if (selectedPaymentMethod.value?.name) {
    next.pay = String(selectedPaymentMethod.value.name)
  }
  const extraValue = Number(extraPremium.value)
  if (timeValue === 0 && Number.isFinite(extraValue) && extraValue > 0) {
    next.extraPremium = String(extraValue)
  }
  if (timeValue === 0 && Number.isInteger(Number(currentPlanId.value)) && Number(currentPlanId.value) > 0) {
    next.addon_plan_id = String(Number(currentPlanId.value))
  }
  if (timeValue === 0 && allPre.value) {
    next.allPre = '1'
  }
  return next
}

const mergePricingQuery = (query: Record<string, any>, pricingQuery: Record<string, string>) => {
  const base = normalizeQuery(query)
  const merged: Record<string, string> = {}
  Object.entries(base).forEach(([key, value]) => {
    if (!pricingQueryKeys.has(key)) {
      merged[key] = value
    }
  })
  Object.assign(merged, pricingQuery)
  return merged
}

const isSameQuery = (a: Record<string, any>, b: Record<string, any>) => {
  const normalizedA = normalizeQuery(a)
  const normalizedB = normalizeQuery(b)
  const keysA = Object.keys(normalizedA)
  const keysB = Object.keys(normalizedB)
  if (keysA.length !== keysB.length) {
    return false
  }
  return keysA.every((key) => normalizedA[key] === normalizedB[key])
}

const selectedCate = computed({
  get: () => pricing.cate,
  set: (val) => {
    pricing.cate = String(val || '')
  },
})
const selectedPlan = computed({
  get: () => pricing.plan as any,
  set: (val) => {
    pricing.plan = val as any
  },
})
const selectedTime = computed({
  get: () => pricing.time,
  set: (val) => {
    pricing.time = Number(val)
  },
})
const promoCode = computed({
  get: () => pricing.promoCode,
  set: (val) => {
    pricing.promoCode = String(val || '')
  },
})
const mode = computed({
  get: () => pricing.mode,
  set: (val) => {
    pricing.mode = val === 'topUp' ? 'topUp' : 'buyService'
  },
})
const selectedPaymentMethod = computed({
  get: () =>
    pricing.selectedPaymentMethod as {
      currency?: string
      name?: string
    },
  set: (val) => {
    pricing.selectedPaymentMethod = (val || {}) as any
  },
})
const topUpAmount = computed({
  get: () => pricing.topUpAmount,
  set: (val) => {
    pricing.topUpAmount = Number(val)
  },
})
const signModalVisible = ref(false)
const defaultState = ref('signup' as 'signup' | 'login')

// Checkout lifecycle (agreements, billing, price confirm, channels, polling)
// shared with the purchase dialog. See composables/usePurchaseFlow.ts.
const {
  confirming,
  showQr,
  qrData,
  dataAddonPurchaseBlocked,
  showPaymentMethodSelector,
  willShowBilling,
  pay,
} = usePurchaseFlow({
  billingEditor,
  onRequireLogin: () => { signModalVisible.value = true },
})
/*
const faqItems = ref([
  {
    key: 'ziranyue',
    status: 'closed',
  },
  {
    key: 'premium_data',
    status: 'closed',
  },
  {
    key: 'billing',
    status: 'closed',
  },
  // {
  //   key: 'ouyuan',
  //   status: 'closed',
  // },
  {
    key: 'daozhang',
    status: 'closed',
  },
  {
    key: 'xueshu',
    status: 'closed',
  },
  {
    key: 'gongyong',
    status: 'closed',
  },
])
*/
const loggedIn = computed(() => session.loggedIn)
const hasService = computed(() => service.hasService)
const extraPremium = computed(() => Number(pricing.extraPremium))
const allPre = computed(() => pricing.allPre)
const feeList = computed(() => $config.feeList)
const currentService = computed(() => pricing.currentService)
const dataAddonValidUntil = computed(() => currentService.value.id
  ? dayjs(dataAddonPeriodEnd(currentService.value)).tz(BILLING_TZ).format('YYYY-MM-DD HH:mm')
  : null)
const currentPlanId = computed(() => currentService.value?.plan_id)
const addonPlanId = computed(() => selectedCate.value === 'home' ? HOME_QUOTE_PLAN_IDENTIFIER : RELAY_DATA_ADDON_PLAN_IDENTIFIER)
const addonSwitchLabelKey = computed(() => selectedCate.value === 'home' ? 'pricing.switch_to_data_addon' : 'pricing.switch_to_addons')
const addonStripTitleKey = computed(() => selectedCate.value === 'home' ? 'pricing.addon_strip_title_home' : 'pricing.addon_strip_title')
const addonStripHintKey = computed(() => selectedCate.value === 'home' ? 'pricing.addon_strip_hint_home' : 'pricing.addon_strip_hint')
const addonStripActiveTitleKey = computed(() => selectedCate.value === 'home' ? 'pricing.addon_strip_active_title_home' : 'pricing.addon_strip_active_title')
const canShowAddonStrip = computed(() => {
  if (selectedCate.value === 'allplatform') {
    return currentService.value?.status === 'on' || Number(currentPlanId.value) === 4
  }
  if (selectedCate.value === 'home') {
    return [5, 6].includes(Number(currentPlanId.value))
  }
  return false
})
const purchaseType = computed(() => pricing.purchaseType)
const purchaseButtonType = computed(() => {
  if (purchaseType.value.alert) {
    return 'warning'
  }
  return 'success'
})

const showSummary = computed(() => {
  if (mode.value === 'topUp') {
    return true
  }
  if (pricing.storeManagedPlan) {
    return false
  }
  if (selectedTime.value === 0) {
    return Boolean(selectedCate.value && selectedPlan.value && pricing.dataAddonQuote.ok)
  }
  return Boolean(selectedCate.value && selectedPlan.value && selectedTime.value)
})

const pleaseUpgradeNotExtend = computed(() => {
  if (currentService.value?.status !== 'off') {
    return false
  }
  if (![2, 5].includes(currentPlanId.value)) {
    return false
  }
  return currentService.value?.extra?.buy_length > 1
})

const summaryDockOnly = computed(() => {
  return showSummary.value && !showPaymentMethodSelector.value && !willShowBilling.value
})

watch(selectedCate, (cate) => {
  const selectedProduct = String(selectedPlan.value)
  const isMatchingAddon = selectedTime.value === 0 && (
    (cate === 'allplatform' && [RELAY_DATA_ADDON_PLAN_IDENTIFIER, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER].includes(selectedProduct as any))
    || (cate === 'home' && selectedProduct === HOME_QUOTE_PLAN_IDENTIFIER)
  )
  if (isMatchingAddon) {
    if (cate === 'home') pricing.allPre = false
    return
  }
  selectedPlan.value = {
    allplatform: 4,
    home: 6,
  }[cate as 'allplatform' | 'home']
  if (cate === 'home') {
    pricing.allPre = false
  }
  // mode.value = 'normalPlan'
  // this.selectedPaymentMethod = this.paymentMethods[0]
  selectedTime.value = 12
})

watch(selectedTime, (t) => {
  // console.log('===> selectedTime changed:', t)
  if (t === 0) {
    if (![RELAY_DATA_ADDON_PLAN_IDENTIFIER, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, HOME_QUOTE_PLAN_IDENTIFIER].includes(String(selectedPlan.value) as any)) {
      pricing.selectDataAddonProduct(addonPlanId.value)
    }
  } else if ([RELAY_DATA_ADDON_PLAN_IDENTIFIER, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, HOME_QUOTE_PLAN_IDENTIFIER].includes(String(selectedPlan.value) as any)) {
    selectedPlan.value = selectedCate.value === 'home' ? 6 : 4
  }
})


watch(
  () => $route.query,
  (query) => {
    if (!isPageQueryActive()) return
    if (pushingQuery.value) {
      pushingQuery.value = false
      return
    }
    applyingQuery.value = true
    pricing.fromQuery(query as any)
    applyingQuery.value = false
  },
  { immediate: true, deep: true },
)

watch(
  () => [
    mode.value,
    selectedCate.value,
    selectedPlan.value,
    selectedTime.value,
    promoCode.value,
    topUpAmount.value,
    selectedPaymentMethod.value?.name,
    extraPremium.value,
    currentPlanId.value,
    allPre.value,
  ],
  () => {
    if (!isPageQueryActive() || !querySyncEnabled.value || applyingQuery.value) {
      return
    }
    const pricingQuery = buildPricingQuery()
    const nextQuery = mergePricingQuery($route.query as any, pricingQuery)
    if (isSameQuery($route.query as any, nextQuery)) {
      return
    }
    pushingQuery.value = true
    navigateTo({ path: $route.path, query: nextQuery }, { replace: true })
  },
)

// watch(extraPremium, (num) => {
//   if (num > maxPreForCurrentMonth.value) {
//     extraPremium.value = maxPreForCurrentMonth.value
//   }
// })

// watch(selectedPlan, (val) => {
//   if (val === currentPlanId.value && pleaseUpgradeNotExtend.value) {
//     console.log('????')
//   }
// })

// watch(() => purchaseType.value.type, (type) => {
//   console.log('===> purchaseType changed:', type)
// })

onMounted(async () => {
  const hadSelectedCate = Boolean(selectedCate.value)
  querySyncEnabled.value = true
  // Reuse the ready snapshot for this target until an explicit invalidation or page reload.
  pricing.loadPricingConfig(session.isAdmin ? Number(user.id) : undefined)
  if (session.loggedIn) {
    await service.getCurrentUserService({ refresh: false })
  }
  // Recheck after loading so a customer's selection takes precedence over the default.
  if (!selectedCate.value) {
    const activeCate = session.loggedIn
      ? (service.currentService?.status === 'on' ? service.currentService.cate : service.usableServiceCate[0])
      : undefined
    selectedCate.value = activeCate || 'allplatform'
  }
  if (hadSelectedCate) {
    useScrollTo({ el: '#plan', offset: -200, mobileOnly: true })
  }
})

const floatingContainer = ref()

function switchDataOrPlan() {
  if (selectedTime.value) {
    pricing.selectDataAddonProduct(addonPlanId.value)
    return
  }
  selectedTime.value = 12
  selectedPlan.value = selectedCate.value === 'home' ? 6 : 4
}
function logout() {
  useDialog().confirm({
    body: $t('settings_page.logout.warning.content'),
    title: $t('settings_page.logout.warning.title'),
    confirmButtonText: $t('settings_page.logout.warning.title'),
    cancelButtonText: $t('service_selector.cancel'),
    defaultFocusButton: 'cancel',
  }).then(
    (ret) => {
      if (ret === 'confirm') {
        session.logout(false)
        pricingToast({
          title: $t('settings_page.logout.suc'),
        })
        location.reload()
      }
    },
    () => { }
  )
}
function upgradePlan() {
  console.log('upgradePlan called.')
  selectedPlan.value = 4
  selectedTime.value = 1
}

function contactEnterprisePricing() {
  openLiveChat()
}

async function openLiveChat() {
  // if (contactedForEnterprisePricing.value) {
  //   return useIntercom().showMessages()
  // }
  // contactedForEnterprisePricing.value = true
  contactingForEnterprisePricing.value = true
  try {
    if (session.loggedIn) {
      console.log('openLiveChat: logged in, creating intercom conversation')
      const { conversation_id } = await user.createIntercomConversation({
        content: $t('pricing.corp_purchase_auto_message'),
      })
      console.log('openLiveChat: conversation_id', conversation_id)
      if (!conversation_id) {
        console.log('openLiveChat: no conversation_id returned, create intercom message by user instead')
        return useIntercom().showNewMessage?.($t('pricing.corp_purchase_auto_message'))
      }
      setTimeout(() => {
        useIntercom().showConversation(conversation_id)
      }, 3 * 1000)
    } else {
      console.log('openLiveChat: not logged in, showing intercom message')
      useIntercom().showNewMessage?.($t('pricing.corp_purchase_auto_message'))
    }
  } catch (e) {
    console.log('openLiveChat: error', e)
  }
  contactingForEnterprisePricing.value = false
  const ret = await useDialog().confirm({
    title: $t('pricing.corp_purchase_auto_message_title'),
    body: $t('pricing.corp_purchase_auto_message_description'),
    confirmButtonText: $t('pricing.corp_purchase_auto_message_confirm'),
    cancelButtonText: $t('pricing.corp_purchase_auto_message_cancel'),
  })
  // 打开
  if (ret === 'confirm') {
    // contactedForEnterprisePricing.value = true
    // useRouter().push('/knowledges/60')
    $utils.openSite('/knowledges/60')
  }
}
// onMounted(async () => {
//   afterSuccessPayment()
// })
</script>

<style lang="postcss" scoped>

/* --- Modern editorial accents (aligned with the landing page) --- */
.eyebrow-label {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  color: rgba(15, 23, 42, 0.62);
  margin-bottom: 1rem;
}

.dark .eyebrow-label {
  color: rgba(255, 255, 255, 0.62);
}

.eyebrow-line {
  width: 2.6rem;
  height: 1px;
  background: color-mix(in srgb, var(--pricing-accent) 52%, transparent);
  flex: none;
}

/* Negative tracking is for Latin display type; neutralise it on CJK headings */
#pricing.is-zh .intro h1,
#pricing.is-zh .title h1 {
  letter-spacing: normal;
}

main {
  width: 100%;
  margin: 0 auto 16px;
  padding: 0 2rem;
  overflow: visible;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    /* margin-top: 34px; */
    padding: 0 1rem;
  }

  color: var(--primary-color);
  max-width: var(--primary-max-width);
  /* padding: 45px 0px; */
}

section {
  margin-bottom: 60px;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-flow: row wrap;
  position: relative;

  /* Remove any glass effect from summary section */
  &.summary {
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
    box-shadow: none !important;
  }

  /* Add spacing for sections without glass effect */
  &:not(.intro) {
    padding: 2rem 0;

    @media (max-width: 768px) {
      padding: 1.5rem 0;
      margin-bottom: 40px;
    }
  }

  /* Ensure proper spacing between plan options */
  .price-table {
    margin: 0 -0.5rem;
  }

  .extra-option {
    text-align: right;
  }

  &:not(&.intro) {
    max-width: var(--primary-max-width);
  }

  h1 {
    font-size: 36px;
    line-height: 46px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    text-align: left;
    color: var(--primary-color);
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 20px;
      line-height: 25px;
    }
  }

  .title {
    h1 {
      font-family: 'Metropolis', 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Microsoft YaHei', 'SF Pro Display', sans-serif;
      font-size: clamp(1.5rem, 3vw, 2rem);
      font-weight: 500;
      letter-spacing: -0.02em;
      gap: 12px;

      span {
        --size: 28px;

        flex: none;
        height: var(--size);
        width: var(--size);
        padding: 0;
        font-family: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace;
        font-size: 13px;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        line-height: 1;
        color: var(--pricing-muted);
        background: transparent;
        border: 1px solid var(--pricing-border);
        border-radius: 8px;
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }
    }
  }

  &.intro {
    text-align: left;
    margin-bottom: 48px;

    h1 {
      font-family: 'Metropolis', 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Microsoft YaHei', 'SF Pro Display', sans-serif;
      font-weight: 500;
      font-size: clamp(2.5rem, 5vw, 4rem);
      line-height: 1.04;
      letter-spacing: -0.03em;
      max-width: 100%;
      margin-bottom: 14px;
      text-wrap: balance;

      @media (max-width: 768px) {
        word-wrap: break-word;
      }
    }

    h2,
    h3 {
      font-weight: 300;
      font-size: 1.05rem;
      color: var(--secondary-color);
    }

    h3 {
      cursor: pointer;
      transition: color 0.2s ease;
    }

    h3:hover {
      color: var(--primary-color);
    }
  }
}


.center {
  text-align: center !important;
}

.pay {
  margin: 20px auto 0;
  padding: 2rem 1.5rem;
  font-size: 1.4rem;
  line-height: 1.4rem;
  width: 100%;


  &.floating {
    box-shadow: var(--shadow-large) !important;
    /* transform: scale(2); */
    /* position: absolute; */
    /* left: -10%; */
    /* right: 0; */
    /* margin: 0 auto; */
    /* padding: 50px; */
    /* width: 100vw; */
    /* margin-left: calc(710px-100vw); */
    /* margin-bottom: 100px; */
    /* bottom: 100px; */
    transition: 200ms;
  }
}

.xueshu-warning {
  margin-top: 20px;

  h1 {
    font-weight: bold !important;
    color: var(--carrot) !important;
    margin-bottom: 20px !important;
  }

  h3 {
    font-weight: 500;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
}

.all-pre-check {
  font-size: 1.5rem;
  justify-content: center;
  vertical-align: middle;

  b {
    font-size: 1.5rem;

    color: var(--primary-color);
  }
}

.hint-for-basic {
  text-align: center;
}

</style>

<style lang="postcss">

#pricing {
  width: 100%;
  position: relative;
  z-index: 60;
  isolation: isolate;

  /* Selectable cards (service type / plan / billing period) */
  .select-mark {
    transition:
      box-shadow 150ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 150ms cubic-bezier(0.22, 1, 0.36, 1);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .dark & .select-mark {
    box-shadow: none;
  }

  /* padding: 2px; */

  /* Frosted glass effect removed - using Shadcn Card instead */

  /* Payment method section - no container styling */
  .payment-method {
    padding: 0 0.5rem;
    margin-bottom: 1rem;
    background: transparent !important;
    box-shadow: none !important;


    /* Ensure nested elements don't have shadows */
    * {
      box-shadow: none !important;
    }

    /* Re-enable shadows only for actual payment option cards */
    .card {
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;

      &.selected {
        box-shadow: inset 0 0 0 1px var(--pricing-accent) !important;
      }
    }
  }

  /* Keep glass effect for billing and details */
  .billing,
  .details {
    padding: 0 0.5rem;
  }

  /* Remove margin-bottom specifically for .summary .details */
  &.summary .details {
    margin-bottom: 0;
  }

  /* Restore row spacing */
  .payments-row {
    margin: 0 -0.5rem;
  }

  /* Remove margin-bottom from time option cards */
  .select-time .time-option>div {
    margin-bottom: 0;
  }

  /* Enhanced glass effect for selected plan cards */
  .select-mark {

    /* Hide checkmark by default, hide choose button always */
    .checkmark {
      display: none;
    }

    .choose {
      display: none;
    }

    &.selected {
      position: relative;

      box-shadow: 0 0 0 1px var(--pricing-accent), inset 0 0 0 1px var(--pricing-accent) !important;

      /* Show checkmark for selected items */
      .checkmark {
        display: block;
        position: absolute;
        bottom: 20px;
        right: 20px;
        font-size: 2rem;
        color: var(--pricing-accent-foreground);
        background: var(--pricing-accent);
        border-radius: 50%;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        z-index: 10;
      }
    }

  }

  /* Fix spacing for category and time options */
  .cate-option,
  .time-option {
    /* padding: 0 0.75rem !important; */
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      padding: 0 !important;
    }
  }

  /* Special handling for time options with discount badges */
  .time-option {
    /* Allow overflow for badges */

    /* Hide checkmark for time options - border is enough */
    .select-mark.selected {
      .checkmark {
        display: none !important;
      }
    }
  }

  /* Remove glass effect from price tags - too prominent */
  .pricetag {
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0.5rem 0;
    margin: 0.5rem 0;

    h1,
    span,
    small {
      background: transparent !important;
      backdrop-filter: none !important;
    }
  }

  /* Ensure price table cards don't have double glass effect on price */
  .price-table {
    .pricetag {
      background: transparent !important;
      backdrop-filter: none !important;
      border-radius: 0 !important;
    }
  }

  .braintree {
    .btn {
      font-size: 1rem;
      padding: 1rem 2rem;
    }
  }

  .qr-modal {
    max-width: 500px;
    /* margin: 0 auto; */

    @media (max-width: 768px) {
      max-width: 100%;
    }
  }

  /* Modern pay button styling */
  .modern-pay-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem 2rem;
    background: var(--pricing-accent-gradient);
    border: none;
    border-radius: 16px;
    color: white;
    font-size: 1.0625rem;
    font-weight: 600;
    cursor: pointer;
    transition:
      background-color 150ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 150ms cubic-bezier(0.22, 1, 0.36, 1);
    box-shadow: none;
    position: relative;
    overflow: hidden;

    &:hover:not(:disabled) {
      background: var(--pricing-accent-hover-gradient);
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &:disabled,
    &.loading {
      opacity: 0.7;
      cursor: not-allowed;
      background: var(--pricing-accent-gradient);
    }

    /* Warning state (for alerts) */
    &.warning:not(.downgrade) {
      background: var(--pricing-alert-gradient);

      &:hover:not(:disabled) {
        background: var(--pricing-alert-hover-gradient);
      }
    }

    /* Downgrade state (danger action) */
    &.downgrade {
      background: var(--pricing-danger-gradient);
      color: var(--pricing-white);

      &:hover:not(:disabled) {
        background: var(--pricing-danger);
      }
    }

    .cart-icon,
    .loading-icon,
    .warning-icon,
    .bolt-icon {
      font-size: 1.125rem;
      position: relative;
      z-index: 1;
    }

    .loading-icon {
      animation: spin 1s linear infinite;
    }

    span {
      position: relative;
      z-index: 1;
    }

  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  /* Button wrapper for proper centering */
  #button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  /* Button positioning with CSS sticky */
  #floating-container {
    position: sticky;
    bottom: 20px;
    z-index: 100;
    /* margin: 20px 0; */
    width: 100%;
  
    @media (max-width: 768px) {
      bottom: 10px;
      /* margin: 10px 1rem; */
      /* width: calc(100% - 2rem); */
    }
  }

  #floating-div {
    width: 100vw;
    /* margin: 0 auto; */
    /* background-color: white;
    padding: 50px;
    margin: 0 auto;
    height: 500px;
    bottom: 100px;
    */
    /* position: absolute; */
    /* left: -100%; */
  }
}

body:has(#pricing) .nuxt-page-wrap.default-page-template-wrap {
  justify-content: flex-start;
}

/* --pricing-* variables are defined globally in assets/css/tailwind.css
   (shared with PurchaseDialog via the .pricing-vars class) */


#pricing .pricing-main {
  box-sizing: border-box;
  width: 100%;
  max-width: calc(1180px + 26rem + 3rem + 4rem);
  margin: 0 auto 4rem;
  padding: 0 2rem;
  color: var(--pricing-text);
}

#pricing .pricing-main *,
#pricing .pricing-main *::before,
#pricing .pricing-main *::after {
  box-sizing: border-box;
}

#pricing .pricing-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(22rem, 26rem);
  align-items: start;
  gap: 3rem;
}

#pricing .pricing-flow {
  min-width: 0;
  width: min(100%, 1180px);
  margin-inline: auto;
}

#pricing .pricing-layout,
#pricing .pricing-flow,
#pricing .step-panel,
#pricing .select-cate,
#pricing .select-plan,
#pricing .select-cate>div,
#pricing .select-plan>div,
#pricing .cate-option,
#pricing .modern-pricing-cards,
#pricing .modern-pricing-card,
#pricing .pricing-summary {
  min-width: 0;
  max-width: 100%;
}

#pricing section.step-panel,
#pricing section.admin-operations,
#pricing section.pricing-help {
  max-width: none;
  margin: 0 0 4rem;
  padding: 0;
  text-align: left;
}

#pricing .section-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

#pricing .section-heading-row .title h1 {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: 'Metropolis', 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Microsoft YaHei', 'SF Pro Display', sans-serif;
  font-size: 1.75rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.15;
  color: var(--pricing-text);
}

#pricing .section-heading-row .title h1 span {
  flex: none;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--pricing-border);
  background: transparent;
  color: var(--pricing-muted);
  font-family: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

#pricing .extra-option {
  flex: none;
}

#pricing .addon-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin: 0.5rem 0 1.7rem;
  padding: 1rem 1.1rem;
  border: 1px dashed var(--pricing-border);
  border-radius: 0.9rem;
  background: color-mix(in oklch, var(--pricing-surface-strong) 72%, transparent);
  cursor: pointer;
  transition:
    border-color 150ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 150ms cubic-bezier(0.22, 1, 0.36, 1);
}

#pricing .addon-strip:hover,
#pricing .addon-strip:focus-visible {
  border-color: var(--pricing-accent-line);
  outline: none;
}

#pricing .addon-copy {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.95rem;
}

#pricing .addon-icon {
  flex: none;
  width: 2.05rem;
  height: 2.05rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  color: var(--pricing-text);
  font-size: 1.25rem;
}

#pricing .addon-copy h3 {
  margin: 0;
  color: var(--pricing-text);
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.2;
}

#pricing .addon-copy p {
  margin: 0.18rem 0 0;
  color: var(--pricing-muted);
  font-size: 0.82rem;
  line-height: 1.35;
}

#pricing .addon-action {
  flex: none;
  min-height: 2.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.9rem;
  border-radius: 0.55rem;
  background: var(--pricing-white-highlight-soft);
  color: var(--pricing-text);
  font-size: 0.8125rem;
  font-weight: 500;
  pointer-events: none;
}

#pricing .rev-ghost {
  min-height: 2.6rem;
  border-radius: 999px;
  border: 1px solid var(--pricing-border);
  background: var(--pricing-surface);
  color: var(--pricing-text);
  box-shadow: none;
  transition:
    border-color 150ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 150ms cubic-bezier(0.22, 1, 0.36, 1);
}

#pricing .rev-ghost:hover {
  border-color: color-mix(in srgb, var(--pricing-text) 16%, transparent);
  background: var(--pricing-solid-surface-strong);
}

#pricing .pricing-surface,
#pricing .summary-card,
#pricing .cate-option [data-slot='card'] {
  border: 1px solid var(--pricing-border);
  background: var(--pricing-solid-surface);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.dark #pricing .pricing-surface,
.dark #pricing .summary-card,
.dark #pricing .cate-option [data-slot='card'] {
  background: #141415;
  box-shadow: none;
}

#pricing .pricing-surface {
  border-radius: 1.5rem;
  padding: 1.5rem;
}

#pricing .plan-empty {
  display: grid;
  min-height: 9rem;
  place-items: center;
  align-content: center;
  gap: 0.75rem;
  color: var(--pricing-muted);
  text-align: center;
}

#pricing .plan-empty p {
  margin: 0;
}

#pricing .cate-option [data-slot='card'] {
  width: 100%;
  max-width: 100%;
  min-height: 12rem;
  overflow: hidden;
}

#pricing .cate-option [data-slot='card-content'] {
  display: flex;
  min-height: 12rem;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  text-align: left;
}

#pricing .cate-option h2 {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: left;
}

#pricing .cate-option h3.detail {
  margin: 0;
  color: var(--pricing-muted);
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.5rem;
}

#pricing .cate-option .site-icons {
  justify-content: flex-start;
  margin: 0;
  min-height: 1.75rem;
}

#pricing .select-mark.selected,
.dark #pricing .select-mark.selected {
  border-color: var(--pricing-accent);
  box-shadow: inset 0 0 0 1px var(--pricing-accent) !important;
}

#pricing .pricing-summary {
  --pricing-action-bottom: 0.75rem;
  --pricing-action-gap: 0.75rem;
  --pricing-action-height: 4rem;

  position: sticky;
  top: 5.5rem;
  width: min(26rem, calc(100vw - 4rem));
  max-height: calc(100vh - 7.5rem);
  max-height: calc(100dvh - 7.5rem);
  align-self: start;
  z-index: 5;
}

#pricing .pricing-summary.is-ready {
  max-height: calc(100vh - 5.5rem - var(--pricing-action-bottom) - var(--pricing-action-gap) - var(--pricing-action-height));
  max-height: calc(100dvh - 5.5rem - var(--pricing-action-bottom) - var(--pricing-action-gap) - var(--pricing-action-height));
}

#pricing .summary-card {
  display: grid;
  gap: 1.25rem;
  width: 100%;
  max-height: inherit;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 1.6rem;
  padding: 1.65rem;
  scrollbar-gutter: stable;
}

#pricing .summary-card-header {
  display: grid;
  gap: 0.35rem;
}

#pricing .block-title {
  margin: 0;
  color: var(--pricing-muted);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
}

#pricing .summary-card h2 {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.18;
}

#pricing .summary-empty {
  display: grid;
  justify-items: start;
  gap: 0.85rem;
  min-height: 14rem;
  align-content: center;
  color: var(--pricing-muted);
  font-size: 0.96rem;
  line-height: 1.55;
}

#pricing .summary-empty .iconify {
  font-size: 1.5rem;
  color: var(--pricing-muted);
}

#pricing .summary-empty p {
  margin: 0;
}

#pricing .summary-block {
  display: grid;
  gap: 0.85rem;
}

#pricing .summary-block+.summary-block {
  padding-top: 1.2rem;
  border-top: 1px solid var(--pricing-border);
}

#pricing .summary-block+.summary-block.billing {
  padding-top: 1.8rem;
  border-top: 0;
}

#pricing .summary-block.billing.billing-attention {
  animation: pricing-billing-attention 0.7s ease-in-out 0s 3;
  border-radius: 1rem;
}

@keyframes pricing-billing-attention {

  0%,
  100% {
    background: transparent;
    box-shadow: none;
  }

  45% {
    background: color-mix(in srgb, var(--pricing-danger) 11%, transparent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--pricing-danger) 55%, transparent);
  }
}

@media (prefers-reduced-motion: reduce) {
  #pricing .summary-block.billing.billing-attention {
    animation: none;
    background: color-mix(in srgb, var(--pricing-danger) 10%, transparent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--pricing-danger) 45%, transparent);
  }
}

#pricing .summary .payment-method,
#pricing .summary .billing,
#pricing .summary .details {
  padding: 0;
}

#pricing .summary .billing :deep(.updatebilling-container) {
  min-height: 0;
}

#pricing .summary .payment-method {
  margin-bottom: 0;
}

#pricing .summary .payment-method * {
  box-shadow: revert-layer;
}

#pricing .summary .details {
  min-width: 0;
}

#pricing .summary .details h3 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: baseline;
  margin: 0 0 0.75rem;
  color: var(--pricing-text);
  font-size: 0.9375rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  text-align: right;
}

#pricing .summary .details h3 .title {
  position: static;
  color: var(--pricing-muted);
  font-size: 0.875rem;
  font-weight: 400;
  justify-self: start;
  text-align: left;
}

#pricing .summary .details h3> :not(.title) {
  justify-self: end;
  text-align: right;
}

#pricing .summary .details h3.subtitle.secondary>small {
  grid-column: 1 / -1;
  justify-self: end;
  max-width: 100%;
  text-align: right;
  overflow-wrap: anywhere;
}

#pricing .summary .details h3.subtitle.secondary {
  margin-bottom: 0.2rem;
  line-height: 1.15;
}

#pricing .summary .details h3.subtitle.secondary:not(.payment-note)+h3.subtitle.secondary:not(.payment-note) {
  margin-top: -0.1rem;
}

#pricing .summary .details h3.payment-note {
  margin-bottom: 0.15rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
}

#pricing .summary .details h3.payment-note.is-first {
  margin-top: 0.55rem;
}

#pricing .summary .details h3.payment-note>small {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}

#pricing .summary .details h3.large {
  margin-top: 0.9rem;
  padding-top: 1rem;
}

#pricing .summary .details h3.large span:last-of-type {
  font-size: 2rem;
  font-weight: 500;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

#pricing #button-wrapper {
  display: block;
  width: 100%;
}

#pricing #floating-container {
  position: static;
  display: grid;
  gap: 0.75rem;
  width: 100%;
}

#pricing #floating-container .summary-block.details {
  min-width: 0;
  padding: 0;
  border-top: 0;
}

#pricing .modern-pay-btn {
  position: absolute;
  top: calc(100% + var(--pricing-action-gap));
  left: 0;
  width: 100%;
  height: var(--pricing-action-height);
  min-height: 4rem;
  margin-top: 0;
  padding-block: 0;
  border-radius: 1.15rem;
  color: var(--pricing-accent-foreground);
  font-weight: 600;
  box-shadow: none;
}

#pricing .modern-pay-btn .bolt-icon {
  color: var(--pricing-accent-foreground);
  text-shadow: none;
}

#pricing .pricing-help {
  grid-column: 1;
  text-align: center;
}

@media (min-width: 769px) {
  #pricing section.pricing-help {
    width: 100%;
    max-width: 46rem;
    margin-right: auto;
    text-align: left;
  }

  #pricing .pricing-help .faq {
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  #pricing .pricing-help .faq h2.title {
    justify-content: flex-start;
  }

  #pricing .pricing-help .pricing-help-separator {
    width: 100% !important;
    margin-left: 0;
    margin-right: 0;
  }
}

#pricing .admin-operations {
  border-radius: 1.5rem;
  padding: 1.5rem;
  background: var(--pricing-surface);
}

#pricing .enterprise-fallback {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

@media (max-width: 1180px) {
  #pricing:has(#floating-container) .pricing-main {
    padding-bottom: calc(10rem + env(safe-area-inset-bottom, 0px));
  }

  #pricing .pricing-layout {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  #pricing .pricing-summary {
    position: relative;
    top: auto;
    right: auto;
    width: 100%;
    max-height: none;
    align-self: start;
    transform: none;
  }

  #pricing .pricing-summary.is-ready {
    max-height: none;
  }

  #pricing .summary-card {
    max-height: none;
    overflow-y: visible;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  #pricing .pricing-summary.is-dock-only {
    position: absolute;
    height: 0;
    width: 0;
    min-height: 0;
    margin: 0;
    padding: 0;
    overflow: visible;
  }

  #pricing .pricing-summary.is-dock-only .summary-card {
    display: contents;
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
  }

  #pricing .pricing-summary.is-dock-only .summary-card-header,
  #pricing .pricing-summary.is-dock-only #checkout {
    display: none;
  }

  #pricing .summary .details h3 {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.75rem;
    text-align: right;
  }

  #pricing .summary .details h3.subtitle.secondary {
    display: none;
  }

  #pricing .summary .details h3.subtitle.special-warning {
    display: none;
  }

  #pricing .summary .details h3.large span:last-of-type {
    font-size: 1.65rem;
  }

  #pricing #button-wrapper {
    display: contents;
    height: 0;
    min-height: 0;
  }

  #pricing #floating-container {
    position: fixed;
    display: grid;
    box-sizing: border-box;
    gap: 0.75rem;
    right: 2rem;
    bottom: calc(var(--pricing-action-bottom) + var(--pricing-action-height) + var(--pricing-action-gap) + env(safe-area-inset-bottom, 0px));
    left: 2rem;
    z-index: 70;
    width: auto;
    max-height: calc(100dvh - 1rem - var(--pricing-action-bottom) - var(--pricing-action-height) - var(--pricing-action-gap) - env(safe-area-inset-bottom, 0px));
    overflow: visible;
  }

  #pricing #floating-container .summary-block.details {
    max-height: calc(100dvh - 1rem - var(--pricing-action-bottom) - var(--pricing-action-height) - var(--pricing-action-gap) - env(safe-area-inset-bottom, 0px));
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  #pricing .modern-pay-btn {
    position: fixed;
    top: auto;
    right: 2rem;
    bottom: calc(var(--pricing-action-bottom) + env(safe-area-inset-bottom, 0px));
    left: 2rem;
    z-index: 70;
    width: auto;
    min-height: var(--pricing-action-height);
    margin-top: 0;
  }

  body:has(#pricing #floating-container) #footer.home-footer {
    padding-bottom: calc(10rem + env(safe-area-inset-bottom, 0px));
  }
}

@media (max-width: 768px) {
  #pricing .pricing-summary {
    --pricing-action-bottom: 0.5rem;
    --pricing-action-gap: 0.65rem;
  }

  #pricing .pricing-main {
    padding: 0 1rem calc(10rem + env(safe-area-inset-bottom, 0px));
  }

  #pricing section.step-panel,
  #pricing section.admin-operations,
  #pricing section.pricing-help {
    margin-bottom: 3rem;
  }

  #pricing .section-heading-row {
    align-items: center;
    flex-direction: row;
    gap: 0.75rem;
  }

  #pricing .section-heading-row .title {
    min-width: 0;
    flex: 1 1 auto;
  }

  #pricing .section-heading-row .title h1 {
    gap: 0.55rem;
    font-size: 1.35rem;
  }

  #pricing .section-heading-row .title h1 span {
    width: 2rem;
    height: 2rem;
    font-size: 0.9rem;
  }

  #pricing .section-heading-row .extra-option {
    flex: 0 0 auto;
  }

  #pricing .section-heading-row .rev-ghost {
    min-height: 2.35rem;
    padding: 0 0.85rem;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  #pricing .select-plan .section-heading-row {
    align-items: center;
    flex-direction: row;
    gap: clamp(0.35rem, 2vw, 0.8rem);
  }

  #pricing .select-plan .section-heading-row .title {
    width: auto;
    flex: 0 1 auto;
  }

  #pricing .select-plan .section-heading-row .title h1 {
    align-items: center;
    width: auto;
    font-size: clamp(0.98rem, 4.2vw, 1.35rem);
    line-height: 1.1;
    white-space: nowrap;
  }

  #pricing .select-plan .section-heading-row .title h1 span {
    width: clamp(1.6rem, 7vw, 2rem);
    height: clamp(1.6rem, 7vw, 2rem);
    font-size: clamp(0.72rem, 3vw, 0.9rem);
  }

  #pricing .select-plan .section-heading-row .select-time {
    width: clamp(10.5rem, 58vw, 17rem);
    flex: 1 1 auto;
    min-width: 0;
  }

  #pricing .addon-strip {
    align-items: center;
    flex-direction: row;
    gap: 0.65rem;
    padding: 0.85rem 0.9rem;
  }

  #pricing .addon-copy {
    flex: 1 1 auto;
    gap: 0.65rem;
  }

  #pricing .addon-copy h3 {
    overflow: hidden;
    font-size: 0.9rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  #pricing .addon-action {
    width: auto;
    min-height: 2rem;
    padding: 0 0.65rem;
    font-size: 0.78rem;
    white-space: nowrap;
  }

  #pricing .addon-copy p {
    display: none;
  }

  #pricing .pricing-surface,
  #pricing .summary-card {
    padding: 1.1rem;
    border-radius: 1.25rem;
  }

  #pricing .select-cate>.cate-option {
    display: grid;
    grid-template-columns: minmax(0, 1fr) !important;
  }

  #pricing .cate-option [data-slot='card'],
  #pricing .modern-pricing-card,
  #pricing .modern-pricing-card .card-wrapper,
  #pricing .summary-card {
    width: 100%;
    max-width: calc(100vw - 2rem);
  }

  #pricing .cate-option [data-slot='card-content'] {
    min-height: 10.5rem;
  }

  #pricing .cate-option [data-slot='card'] {
    min-height: 0;
  }

  #pricing #floating-container {
    gap: 0.65rem;
    right: 1rem;
    left: 1rem;
    max-height: calc(100dvh - 1rem - var(--pricing-action-bottom) - var(--pricing-action-height) - var(--pricing-action-gap) - env(safe-area-inset-bottom, 0px));
  }

  #pricing .modern-pay-btn {
    right: 1rem;
    left: 1rem;
  }
}
#pricing .pricing-surface,
#pricing .summary-card,
#pricing .cate-option [data-slot='card'] {
  border-radius: 0.875rem;
}

#pricing .modern-pricing-card {
  --card-radius: 0.875rem;
}

#pricing .modern-pricing-card .upper-section .plan-button,
#pricing .modern-pay-btn,
#pricing .rev-ghost {
  border-radius: 0.625rem;
}

</style>
